import { useEffect, useState, RefObject, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSession } from "@/hooks/useSession";
import {
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { ActivityBar, ActivityView } from "./ActivityBar";
import { SidePanel } from "./SidePanel";
import type { User } from "@supabase/supabase-js";
import { Message } from "@/types/message";
import { CurrentFocusContainerMemento } from "@/types/memento";
import { CurrentFocusContainerHandle } from "./CurrentFocusContainer";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";

interface AppSidebarProps {
  onFocusEditor?: () => void;
  activeView: ActivityView;
  onViewChange: (view: ActivityView) => void;
  currentFocusRef: React.RefObject<CurrentFocusContainerHandle>;
}

export function AppSidebar(props: AppSidebarProps) {
  const { 
    onFocusEditor, 
    activeView, 
    onViewChange, 
    currentFocusRef
  } = props;
  const navigate = useNavigate();
  const { user } = useSession();
  const [messages, setMessages] = useState<Message[]>([]);
  const [mementos, setMementos] = useState<CurrentFocusContainerMemento[]>([]);
  const [mementoPage, setMementoPage] = useState(0);
  const [mementoPageSize] = useState(25);
  const [hasMoreMementos, setHasMoreMementos] = useState(false);
  const [loadingMementos, setLoadingMementos] = useState(false);
  const mementosSubscribedRef = useRef(false);
  const [activeTab, setActiveTab] = useState("timeline");
  const [isSigningOut, setIsSigningOut] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (user) {
      loadMessages();
      // Reset pagination
      setMementoPage(0);
      loadMementos(0);
      const unsubscribeMessages = subscribeToMessages();
      const unsubscribeMementos = subscribeToMementos();
      return () => {
        unsubscribeMessages?.();
        unsubscribeMementos?.();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMessages = async () => {
    if (!user) return;

    try {
      // Load pinned messages separately
      const { data: pinnedData, error: pinnedError } = await supabase
        .from("messages")
        .select("*")
        .eq("user_id", user.id)
        .eq("deleted", false)
        .eq("pinned", true)
        .order("pinned_order", { ascending: true, nullsFirst: false });

      if (pinnedError) throw pinnedError;

      // Load recent unpinned messages - order ascending to avoid reverse
      const { data: recentData, error: recentError } = await supabase
        .from("messages")
        .select("*")
        .eq("user_id", user.id)
        .eq("deleted", false)
        .eq("pinned", false)
        .order("created_at", { ascending: true })
        .limit(100);

      if (recentError) throw recentError;

      const pinnedMessages = pinnedData || [];
      const unpinnedMessages = recentData || [];
      setMessages([...pinnedMessages, ...unpinnedMessages] as Message[]);
    } catch (error: any) {
      console.error("Error loading messages:", error);
      toast.error("Failed to load messages");
    }
  };

  const subscribeToMessages = () => {
    if (!user) return;

    const channel = supabase
      .channel(`messages-sidebar:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newMessage = payload.new as Message;
          setMessages((prev) => {
            // Check if message already exists to prevent duplicates
            if (prev.some((m) => m.id === newMessage.id)) return prev;
            // Insert in chronological order
            return [...prev, newMessage];
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updatedMessage = payload.new as Message;
          setMessages((prev) => {
            // Handle soft deletes as removals
            if (updatedMessage.deleted) {
              return prev.filter((m) => m.id !== updatedMessage.id);
            }
            // Update specific message - handle pinned status changes
            return prev.map((m) => {
              if (m.id === updatedMessage.id) {
                return updatedMessage;
              }
              return m;
            });
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deletedMessage = payload.old as Message;
          setMessages((prev) => prev.filter((m) => m.id !== deletedMessage.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const subscribeToMementos = () => {
    if (!user) return;

    // Prevent duplicate subscriptions in React StrictMode/dev hot reloads
    if (mementosSubscribedRef.current) {
      console.log('[AppSidebar] Memento subscription already active, skipping');
      return () => {};
    }
    mementosSubscribedRef.current = true;

    console.log('[AppSidebar] Setting up memento subscription for user:', user.id);
    const channel = supabase
      .channel(`mementos-sidebar:${user.id}`, {
        config: {
          broadcast: { self: true },
          presence: { key: user.id }
        }
      })
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "current_focus_container_mementos",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[AppSidebar] New memento inserted:', payload.new?.id);
          const incoming = payload.new as any;
          // keep state lightweight: store only summary fields in the list
          const summary = {
            id: incoming.id,
            user_id: incoming.user_id,
            created_at: incoming.created_at,
            trigger_type: incoming.trigger_type,
            custom_title: incoming.custom_title,
            pinned: incoming.pinned,
            favorited: incoming.favorited,
            deleted: incoming.deleted,
          } as CurrentFocusContainerMemento;
          setMementos((prev) => {
            if (prev.some((m) => m.id === summary.id)) return prev;
            console.log('[AppSidebar] Adding memento summary to state, total:', prev.length + 1);
            return [summary as any, ...prev];
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "current_focus_container_mementos",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[AppSidebar] Memento updated:', payload.new);
          const updatedMemento = payload.new as CurrentFocusContainerMemento;
          setMementos((prev) => {
            // Handle soft deletes as removals
            if (updatedMemento.deleted) {
              return prev.filter((m) => m.id !== updatedMemento.id);
            }
            // Update specific memento - MERGE changes instead of replace
            return prev.map((m) => 
              m.id === updatedMemento.id ? { ...m, ...updatedMemento } : m
            );
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "current_focus_container_mementos",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[AppSidebar] Memento deleted:', payload.old);
          const deletedMemento = payload.old as CurrentFocusContainerMemento;
          setMementos((prev) => prev.filter((m) => m.id !== deletedMemento.id));
        }
      )
      .subscribe();

    return () => {
      console.log('[AppSidebar] Cleaning up memento subscription');
      supabase.removeChannel(channel);
      mementosSubscribedRef.current = false;
    };
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Get current pinned messages to avoid race conditions
    const currentPinned = messages
      .filter((m) => m.pinned)
      .sort((a, b) => (a.pinned_order || 0) - (b.pinned_order || 0));
    const oldIndex = currentPinned.findIndex((m) => m.id === active.id);
    const newIndex = currentPinned.findIndex((m) => m.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic update
    const reordered = [...currentPinned];
    const [movedItem] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedItem);

    // Update local state immediately
    const updatedMessages = messages.map((msg) => {
      const newPosition = reordered.findIndex((r) => r.id === msg.id);
      if (newPosition !== -1) {
        return { ...msg, pinned_order: newPosition };
      }
      return msg;
    });
    setMessages(updatedMessages as Message[]);

    // Persist to database
    try {
      const updates = reordered.map((msg, idx) =>
        supabase.from("messages").update({ pinned_order: idx }).eq("id", msg.id)
      );
      await Promise.all(updates);
    } catch (error: any) {
      console.error("Error reordering messages:", error);
      toast.error("Failed to reorder messages");
      loadMessages(); // Revert on error
    }
  };
  const handlePin = async (messageId: string, pinned: boolean) => {
    // Calculate maxOrder once
    const maxOrder = Math.max(
      ...messages.filter((m) => m.pinned).map((m) => m.pinned_order || 0),
      -1
    );

    // Optimistic update
    const updatedMessages = messages.map((msg) => {
      if (msg.id === messageId) {
        if (pinned) {
          return { ...msg, pinned: true, pinned_order: maxOrder + 1 };
        } else {
          return { ...msg, pinned: false, pinned_order: null };
        }
      }
      return msg;
    });
    setMessages(updatedMessages as Message[]);

    // Persist to database
    try {
      if (pinned) {
        const { error } = await supabase
          .from("messages")
          .update({
            pinned: true,
            pinned_order: maxOrder + 1,
          })
          .eq("id", messageId);
        if (error) throw error;
        toast.success("Message pinned");
      } else {
        const { error } = await supabase
          .from("messages")
          .update({
            pinned: false,
            pinned_order: null,
          })
          .eq("id", messageId);
        if (error) throw error;
        toast.success("Message unpinned");
      }
    } catch (error: any) {
      console.error("Error pinning message:", error);
      toast.error("Failed to update message");
      loadMessages(); // Revert on error
    }
  };

  const handleFavorite = async (messageId: string, favorited: boolean) => {
    // Optimistic update
    setMessages(
      messages.map((msg) =>
        msg.id === messageId ? ({ ...msg, favorited } as Message) : msg
      )
    );

    try {
      const { error } = await supabase
        .from("messages")
        .update({ favorited })
        .eq("id", messageId);

      if (error) throw error;
      toast.success(favorited ? "Message favorited" : "Message unfavorited");
    } catch (error: any) {
      console.error("Error favoriting message:", error);
      toast.error("Failed to update message");
      loadMessages(); // Revert on error
    }
  };

  const handleDelete = async (messageId: string) => {
    // Optimistic update
    setMessages(messages.filter((msg) => msg.id !== messageId));

    try {
      const { error } = await supabase
        .from("messages")
        .update({ deleted: true })
        .eq("id", messageId);

      if (error) throw error;
      toast.success("Message deleted");
    } catch (error: any) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message");
      loadMessages(); // Revert on error
    }
  };

  const pinnedMessages = messages
    .filter((m) => m.pinned)
    .sort((a, b) => (a.pinned_order || 0) - (b.pinned_order || 0));

  const handleSignOut = async () => {
    setIsSigningOut(true);

    try {
      // Sign out from Supabase
      await supabase.auth.signOut();

      // Clear data cache (session storage)
      sessionStorage.clear();

      // Clear local storage except for code cache
      const codeCache = localStorage.getItem("vite");
      localStorage.clear();
      if (codeCache) {
        localStorage.setItem("vite", codeCache);
      }

      toast.success("Signed out successfully");

      // Navigate to auth page
      navigate("/auth");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
      setIsSigningOut(false);
    }
  };

  const handleSignIn = () => {
    navigate("/auth");
  };

  // Staged loading strategy for optimal UX:
  // Stage 1: Load with default filters (today only) - instant visible results
  // Stage 2: Background-load full dataset - enables instant filter switching
  const loadMementos = async (page = 0, useDefaultFilters = true) => {
    if (!user) {
      console.log('[AppSidebar] No user, skipping memento load');
      return;
    }

    console.log('[AppSidebar] Loading mementos - stage:', useDefaultFilters ? '1 (filtered)' : '2 (full)', 'page:', page);
    setLoadingMementos(true);
    const from = page * mementoPageSize;
    const to = from + mementoPageSize - 1;
    
    try {
      // Build query with optional default filters
      let query = supabase
        .from("current_focus_container_mementos")
        .select(
          "id,created_at,trigger_type,custom_title,pinned,favorited,user_id"
        )
        .eq("user_id", user.id)
        .eq("deleted", false);

      // Stage 1: Apply default filters for fast initial render
      if (useDefaultFilters && page === 0) {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Default filter: today's items only (small, fast query)
        // Hide auto_save/history_load that are not pinned AND not favorited
        query = query.gte("created_at", todayStart.toISOString());
        
        // Exclude noise: auto_save/history_load that have no user significance
        // Keep if: NOT noise, OR pinned, OR favorited
        query = query.or(
          "trigger_type.not.in.(auto_save,history_load)," +
          "pinned.eq.true," +
          "favorited.eq.true"
        );
      }
      
      // Stage 2: Load ALL data (no noise filter) for instant client-side filter switching

      const { data, error } = await query
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) {
        console.error('[AppSidebar] Query error:', error);
        throw error;
      }

      const items = (data || []) as unknown as CurrentFocusContainerMemento[];
      console.log('[AppSidebar] Loaded', items.length, 'mementos -', useDefaultFilters ? 'stage 1 complete' : 'stage 2 complete');

      if (page === 0) {
        // Stage 1: Replace with filtered data
        // Stage 2: Merge with existing data (don't replace Stage 1's filtered items)
        if (useDefaultFilters) {
          setMementos(items);
        } else {
          // Stage 2 merge: Add new items not already present
          setMementos(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newItems = items.filter(m => !existingIds.has(m.id));
            console.log('[AppSidebar] Stage 2 merge: kept', prev.length, 'existing, added', newItems.length, 'new items');
            return [...prev, ...newItems];
          });
        }
        
        // Stage 2: Load full dataset in background for instant filter switching
        if (useDefaultFilters) {
          setTimeout(() => {
            console.log('[AppSidebar] Starting stage 2: loading full dataset for filter reactivity...');
            loadMementos(0, false); // Fetch unfiltered data
          }, 500); // Delay to let stage 1 UI render first
        }
      } else {
        // Pagination: append to existing data
        setMementos((prev) => [...prev, ...items]);
      }

      setHasMoreMementos(items.length === mementoPageSize);
      setMementoPage(page);
    } catch (error) {
      console.error("[AppSidebar] Error loading mementos:", error);
      toast.error("Failed to load drafts");
    } finally {
      setLoadingMementos(false);
    }
  };

  const loadMoreMementos = () => {
    if (!hasMoreMementos || loadingMementos) return;
    loadMementos(mementoPage + 1, false); // Pagination always loads unfiltered
  };

  // Fetch full memento by id when user wants to open it (progressive enhancement)
  const loadMementoDetails = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from("current_focus_container_mementos")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as unknown as CurrentFocusContainerMemento;
    } catch (error) {
      console.error('[AppSidebar] Error loading memento details:', error);
      throw error;
    }
  };

  // Progressive enhancement for memento loading:
  // Stage 1: Load summary data immediately (instant preview from cached timeline data)
  // Stage 2: Fetch full content in background (ready for editing/viewing)
  const handleLoadMemento = async (memento: CurrentFocusContainerMemento) => {
    if (!memento?.id) return;
    
    try {
      // Stage 1: Load with summary data immediately (what we have from timeline)
      // This provides instant visual feedback
      if (currentFocusRef.current) {
        console.log('[AppSidebar] Stage 1: Loading memento summary into view');
        currentFocusRef.current.loadMemento(memento);
        
        // Stage 2: Fetch full content in background
        // This happens async so user sees instant response, then full data hydrates
        console.log('[AppSidebar] Stage 2: Fetching full memento content...');
        const full = await loadMementoDetails(memento.id);
        console.log('[AppSidebar] Stage 2 complete: Full content loaded');
        
        // Hydrate with full content (updates in place)
        currentFocusRef.current.loadMemento(full);
      } else {
        console.error("CurrentFocusContainer ref not available");
        toast.error("Failed to load draft");
      }
    } catch (error) {
      console.error('[AppSidebar] Error in progressive load:', error);
      toast.error("Failed to load full draft content");
    }
  };

  const handlePinMemento = async (mementoId: string) => {
    const memento = mementos.find((m) => m.id === mementoId);
    if (!memento) return;

    try {
      const { error } = await supabase
        .from("current_focus_container_mementos")
        .update({ pinned: !memento.pinned })
        .eq("id", mementoId);

      if (error) throw error;

      setMementos((prev) =>
        prev.map((m) => (m.id === mementoId ? { ...m, pinned: !m.pinned } : m))
      );

      toast.success(memento.pinned ? "Unpinned draft" : "Pinned draft");
    } catch (error: any) {
      console.error("Error pinning memento:", error);
      toast.error("Failed to pin draft");
    }
  };

  const handleFavoriteMemento = async (mementoId: string) => {
    const memento = mementos.find((m) => m.id === mementoId);
    if (!memento) return;

    try {
      const { error } = await supabase
        .from("current_focus_container_mementos")
        .update({ favorited: !memento.favorited })
        .eq("id", mementoId);

      if (error) throw error;

      setMementos((prev) =>
        prev.map((m) =>
          m.id === mementoId ? { ...m, favorited: !m.favorited } : m
        )
      );

      toast.success(
        memento.favorited ? "Removed from favorites" : "Added to favorites"
      );
    } catch (error: any) {
      console.error("Error favoriting memento:", error);
      toast.error("Failed to favorite draft");
    }
  };

  const handleDeleteMemento = async (mementoId: string) => {
    try {
      // Hard DELETE - soft delete (UPDATE) has RLS issues on this table
      // See: docs/technical/RLS_UPDATE_POLICY_BUG_COMPLETE_ANALYSIS.md
      const { error } = await supabase
        .from("current_focus_container_mementos")
        .delete()
        .eq("id", mementoId);

      if (error) throw error;

      setMementos((prev) => prev.filter((m) => m.id !== mementoId));
      toast.success("Draft deleted");
    } catch (error) {
      console.error("Error deleting memento:", error);
      toast.error("Failed to delete draft");
    }
  };

  return (
    <div className="flex h-full w-full">
      {/* Activity Bar - Always visible */}
      <ActivityBar activeView={activeView} onViewChange={onViewChange} />

      {/* Side Panel - Conditionally visible */}
      {activeView && (
        <div className="border-r flex-1 min-w-0 overflow-hidden">
          <SidePanel
            activeView={activeView}
            user={user}
            messages={messages}
            mementos={mementos}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onFocusEditor={onFocusEditor}
            onLoadMemento={handleLoadMemento}
            onPin={handlePin}
            onFavorite={handleFavorite}
            onDelete={handleDelete}
            onPinMemento={handlePinMemento}
            onFavoriteMemento={handleFavoriteMemento}
            onDeleteMemento={handleDeleteMemento}
            onLoadMoreMementos={loadMoreMementos}
            hasMoreMementos={hasMoreMementos}
            loadingMementos={loadingMementos}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onSignOut={handleSignOut}
            onSignIn={handleSignIn}
            isSigningOut={isSigningOut}
          />
        </div>
      )}
    </div>
  );
}
